import { type ReactNode, useState } from 'react';

import classNames from 'classnames';

import { NavbarMenuItemDropdownV2 } from './components/NavbarMenuItemDropdown';
import { NavbarMenuItemSearchV2 } from './components/NavbarMenuItemSearch';

import * as styles from '../Navbar.css';
import { useNavbarScrollStore } from '../navbarScroll';
import { NavbarMenuItemLinkV2, NavbarMenuItemLogoV2, NavbarMenuItemUserButtonV2 } from './components';
import type {
  NavbarMenuItemV2ButtonElementProps,
  NavbarMenuItemV2ElementProps,
  NavbarMenuItemV2LinkElementProps,
} from './NavbarMenuItem.type';

export function NavbarMenuItemV2(
  props: NavbarMenuItemV2ElementProps & {
    controlbyselected: boolean | string;
    selected: boolean;
    excludeSeparator?: boolean;
    isActive?: boolean;
  },
) {
  const { element, children, attributes, excludeSeparator, isActive, isInEditor } = props;
  const { type, showOnMobile, showAfterScroll, scrollAppearOffset } = element.attrs || {};
  const [isInsideBurgerContent, setIsInsideBurgerContent] = useState(false);

  const shouldRenderDropdownSeparatelyForEditor =
    attributes?.['data-should-render-dropdown-separately-for-editor'] !== undefined;

  const isEditorViewportDesktop = attributes?.['data-viewport'] === 'desktop';

  const needsScrollAppear = !isInEditor && Boolean(showAfterScroll);
  const scroll = useNavbarScrollStore(needsScrollAppear);
  const appearOffset = Number(scrollAppearOffset) || 200;
  const isScrollVisible = !needsScrollAppear || scroll.scrollY >= appearOffset;

  const scrollAppearClassName = needsScrollAppear
    ? classNames(
        styles.navbarItemScrollAppearV2,
        isScrollVisible ? styles.navbarItemScrollVisibleV2 : styles.navbarItemScrollHiddenV2,
      )
    : undefined;

  const wrapScrollAppear = (node: ReactNode) =>
    needsScrollAppear ? <div className={scrollAppearClassName}>{node}</div> : node;

  const layoutClassName = classNames({
    'hidden md:flex': showOnMobile === false && type !== 'user_button',
    'flex items-center': shouldRenderDropdownSeparatelyForEditor && isEditorViewportDesktop,
  });

  const wrapperClassName = classNames(layoutClassName, props.className);

  if (type === 'link' || type === 'button') {
    return wrapScrollAppear(
      <NavbarMenuItemLinkV2
        {...(props as NavbarMenuItemV2LinkElementProps | NavbarMenuItemV2ButtonElementProps)}
        excludeSeparator={excludeSeparator}
        className={wrapperClassName}
        isActive={isActive}
      >
        {children}
      </NavbarMenuItemLinkV2>,
    );
  }
  if (type === 'user_button') {
    return wrapScrollAppear(
      <NavbarMenuItemUserButtonV2 {...props} className={wrapperClassName}>
        {children}
        {element?.attrs?.type === 'user_button' && element?.attrs?.style === 'button' && (
          <div className={styles.navbarItemSeparatorV2} />
        )}
      </NavbarMenuItemUserButtonV2>,
    );
  }
  if (type === 'search') {
    return wrapScrollAppear(
      <NavbarMenuItemSearchV2 {...props} className={wrapperClassName}>
        <div className={styles.navbarItemSeparatorV2} />
      </NavbarMenuItemSearchV2>,
    );
  }
  if (type === 'logo') {
    return wrapScrollAppear(
      <NavbarMenuItemLogoV2 {...props} className={wrapperClassName}>
        {children}
        <div className={styles.navbarItemSeparatorV2} />
      </NavbarMenuItemLogoV2>,
    );
  }

  return wrapScrollAppear(
    shouldRenderDropdownSeparatelyForEditor ? (
      <div className={wrapperClassName || 'contents'}>{children}</div>
    ) : (
      <NavbarMenuItemDropdownV2
        ref={(ref) => setIsInsideBurgerContent(!!ref?.closest('.navbar-menu-burger-content')?.contains(ref))}
        forceShowAccordion={isInsideBurgerContent}
        {...props}
        className={wrapperClassName}
      >
        {children}
      </NavbarMenuItemDropdownV2>
    ),
  );
}
